import { message } from 'antd';

interface ErrorHandlerOptions {
  successMessage?: string;
  errorMessage?: string;
  onSuccess?: () => void;
  onError?: (error: any) => void;
  showSuccessMessage?: boolean;
  showErrorMessage?: boolean;
}

// Track recently shown error messages to prevent duplicates
const recentErrorMessages = new Map<string, number>();
const ERROR_DEDUP_WINDOW_MS = 2000; // 2 seconds

/**
 * Shows an error message, but only if the same message hasn't been shown recently
 */
const showDeduplicatedError = (errorMsg: string) => {
  const now = Date.now();
  const lastShown = recentErrorMessages.get(errorMsg);

  if (lastShown && (now - lastShown) < ERROR_DEDUP_WINDOW_MS) {
    // Same message shown recently, skip
    return;
  }

  // Show message and track it
  message.error(errorMsg);
  recentErrorMessages.set(errorMsg, now);

  // Clean up old entries (older than 5 seconds)
  Array.from(recentErrorMessages.entries()).forEach(([msg, timestamp]) => {
    if (now - timestamp > 5000) {
      recentErrorMessages.delete(msg);
    }
  });
};

/**
 * Centralized error handler for API calls
 * Handles success/error messages and callbacks automatically
 */
export const handleApiCall = async <T>(
  apiCall: () => Promise<T>,
  options: ErrorHandlerOptions = {}
): Promise<T | null> => {
  const {
    successMessage,
    errorMessage,
    onSuccess,
    onError,
    showSuccessMessage = true,
    showErrorMessage = true
  } = options;

  try {
    const result = await apiCall();

    if (showSuccessMessage && successMessage) {
      message.success(successMessage);
    }

    if (onSuccess) {
      onSuccess();
    }

    return result;
  } catch (error: any) {
    console.error('API call error:', error);

    // Re-throw license errors so components can handle them specifically
    // Check both error.response.status (from axios) and error.statusCode (from interceptor)
    const status = error?.response?.status || error?.statusCode;
    if (status === 403) {
      if (onError) {
        onError(error);
      }
      throw error;
    }

    if (showErrorMessage) {
      const errorMsg = error?.message || errorMessage || 'An error occurred';
      showDeduplicatedError(errorMsg);
    }

    if (onError) {
      onError(error);
    }

    return null;
  }
};

/**
 * Simplified error handler for API calls that only need error handling
 * Shortcut of handleApiCall with showSuccessMessage = false
 */
export const handleApiError = async <T>(
  apiCall: () => Promise<T>,
  errorMessage?: string
): Promise<T | null> => {
  return handleApiCall(apiCall, {
    errorMessage,
    showSuccessMessage: false
  });
};

/**
 * Error handler for API calls with success message
 * Shortcut of handleApiCall with showErrorMessage = false
 */
export const handleApiWithSuccess = async <T>(
  apiCall: () => Promise<T>,
  successMessage: string,
  errorMessage?: string
): Promise<T | null> => {
  return handleApiCall(apiCall, {
    successMessage,
    errorMessage,
    showSuccessMessage: true,
    showErrorMessage: true
  });
};
